/**
 * Oversikt for én act (PERFORMANCE.md, fane 1).
 *
 * ---------------------------------------------------------------------------------------------
 * THE TIMETABLE IS DERIVED, AND THAT IS THE POINT OF THE WHOLE TAB.
 *
 * «Punktene genereres fra de andre fanene ... ikke skrives inn på nytt», and the consequence the
 * spec spells out is the one worth protecting: «endres en tid ett sted, endres den her». A flight,
 * a hotel check-in, the dinner and the set are computed on the read from the tables that own them.
 * Only `manual` items are rows of their own — get-in, linecheck, soundcheck, load-out, which no tab
 * owns yet.
 *
 * `source` is on every item so the page can say where a time comes from, and so a reader who wants
 * to change one knows which tab to open. That is not decoration: an item you cannot trace is an
 * item somebody will try to edit here, and here is not where it lives.
 *
 * ---------------------------------------------------------------------------------------------
 * THE GAPS TRAVEL WITH THE ITEM, AND WITH THE COUNTERS.
 *
 * Principle 2 of the page: «uten sjåfør, uten akkreditering, ubekreftet leveranse — alt skal vises
 * der man er». So a local run with no driver carries its own warning in the timetable, and the four
 * stat cards each carry a count of what is missing behind them. A number with no gap marker is a
 * number that looks finished.
 */
import { z } from 'zod';

import { travelModeSchema } from '../enums.js';
import { wireInstant } from './wire-time.js';

/**
 * HVA ET PUNKT I TIMEPLANEN ER (QA13 punkt 72 og 77) — ikke hvilken fane det bor på.
 *
 * Før var det `source`, og middagen het «Advance» fordi den bor på advance-fanen, mens fly og
 * lokal transport delte «Reise». Nå er det hva punktet ER, lest av hendelseslaget (0924):
 * `travel` (reise inn og ut, med `travelMode`), `local_transport`, `check_in`/`check_out`
 * (bare når planlagt tid er satt), `dinner`, `on_stage`/`off_stage` (settet som to rader, med
 * scenetidenes navn), `stage_time` (et punkt med type) og `manual` (et fritt punkt).
 */
export const TIMETABLE_KINDS = [
  'travel',
  'local_transport',
  'check_in',
  'check_out',
  'dinner',
  'on_stage',
  'off_stage',
  'stage_time',
  'manual',
] as const;
export const timetableKindSchema = z.enum(TIMETABLE_KINDS);
export type TimetableKind = z.infer<typeof timetableKindSchema>;

/**
 * Scenetidene — punktene en showdag faktisk kjøres på.
 *
 * ---------------------------------------------------------------------------------------------
 * DE VAR SJU FASTE VERDIER I DENNE FILA, OG DET VAR FEIL.
 *
 * Argumentet for å hardkode dem sto her i et døgn: get-in, soundcheck og curfew er bransjens egne
 * ord, de står på rideren fra motparten før noen hos arrangøren har åpnet Tvitvi, og to festivaler
 * som kalte «on stage» hver sin ting ville sendt regninga til turnélederen med papirutgaven.
 *
 * Sak 68, svart 2. september: **68.2**. De blir organisasjonens egne. Argumentet var for sterkt —
 * en klubb med én scene og en festival med fire kjører ikke samme punkter, og «Bus call» er like
 * mye et bransjeord som «push back». De sju følger fortsatt med som utgangspunkt; forskjellen er
 * at de nå er RADER en organisasjon kan endre framfor en liste bare vi kan endre.
 *
 * ---------------------------------------------------------------------------------------------
 * `binding` ER GRUNNEN TIL AT DETTE IKKE ER EN REN NAVNELISTE.
 *
 * «On stage» og «off stage» ER programpostens `startsAt` og `endsAt` — de samme to feltene
 * kjøreplanen bygges av og timeplanen tegner settet fra. Et punkt bundet dit SKRIVER dit, framfor
 * å bli en rad, og det er derfor acten ikke står to ganger på dagsplanen. Navnet kan endres,
 * bindingen kan ikke, og et bundet punkt kan ikke slettes.
 *
 * Det er den samme formen sak 63.3 beskriver for statuser: navnet folk leser, og et flagg som sier
 * hva plattformen skal gjøre med det.
 */
export const STAGE_TIME_BINDINGS = ['row', 'performance_start', 'performance_end'] as const;
export const stageTimeBindingSchema = z.enum(STAGE_TIME_BINDINGS);
export type StageTimeBinding = z.infer<typeof stageTimeBindingSchema>;

/** How many cues one organisation may keep. A day sheet, not a taxonomy. */
export const MAX_STAGE_TIME_KINDS = 30;

/** Hvor mange scener ett punkt kan krysses av for. Et anlegg, ikke en katalog. */
export const MAX_STAGES_PER_STAGE_TIME = 50;

export const stageTimeKindDtoSchema = z
  .object({
    id: z.uuid(),
    /** Hvilken årgang punktet hører til (K-1). `null` er husets eget sett. */
    editionId: z.uuid().nullable(),
    /** Stable across renames. `get_in`, `load_in` … and whatever the organisation adds. */
    key: z.string(),
    name: z.string(),
    binding: stageTimeBindingSchema,
    sortOrder: z.number().int(),
    /**
     * HVILKE SCENER PUNKTET GJELDER — TOM BETYR ALLE (12d, svar L-2).
     *
     * Aldri `null`: en tom liste og «ikke oppgitt» ville vært to skrivemåter for det samme, og
     * den ene av dem ville før eller siden blitt lest som «ingen scener».
     */
    stageIds: z.array(z.uuid()),
    /** Punktet opptar scenen: en annen act kan ikke spille eller rigge samtidig (253.29). */
    occupiesStage: z.boolean(),
    /** Varigheten et punkt uten sluttid får i tidslinja og konfliktene. Null: et tidspunkt. */
    defaultMinutes: z.number().int().nullable(),
    /** Gjelder scenen, ikke folkene: en strek på scenens tidslinje (Curfew, 253.28). */
    stageLine: z.boolean(),
  })
  .meta({ id: 'StageTimeKind' });
export type StageTimeKindDto = z.infer<typeof stageTimeKindDtoSchema>;

/** Hvilket sett. Påkrevd — se `listAccreditationTypesQuerySchema` for den målte grunnen. */
export const listStageTimeKindsQuerySchema = z
  .object({ editionId: z.uuid() })
  .meta({ id: 'ListStageTimeKindsQuery' });
export type ListStageTimeKindsQuery = z.infer<typeof listStageTimeKindsQuerySchema>;

export const stageTimeKindListResponseSchema = z
  .object({ items: z.array(stageTimeKindDtoSchema) })
  .meta({ id: 'StageTimeKindListResponse' });
export type StageTimeKindListResponse = z.infer<typeof stageTimeKindListResponseSchema>;

const kindName = z.string().trim().min(1).max(60);

/** Standardvarighet i minutter: et døgn er taket, som i basen. */
export const MAX_STAGE_TIME_MINUTES = 1440;
const defaultMinutesSchema = z.number().int().min(1).max(MAX_STAGE_TIME_MINUTES);

export const createStageTimeKindRequestSchema = z
  .object({
    editionId: z.uuid(),
    name: kindName,
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    stageIds: z.array(z.uuid()).max(MAX_STAGES_PER_STAGE_TIME).optional(),
    /** Utelatt: fjorårets valg for samme punkt, ellers standarden (0924). */
    occupiesStage: z.boolean().optional(),
    defaultMinutes: defaultMinutesSchema.nullable().optional(),
    stageLine: z.boolean().optional(),
  })
  .meta({ id: 'CreateStageTimeKindRequest' });
export type CreateStageTimeKindRequest = z.infer<typeof createStageTimeKindRequestSchema>;

/**
 * The name and the order move. The binding and the key do not.
 *
 * A key is what a timetable row points at through history; a binding is what the platform does
 * with the cue. Letting either move would make «rename» a different operation depending on which
 * cue you renamed, which is the kind of surprise a settings screen must not hold.
 */
export const updateStageTimeKindRequestSchema = z
  .object({
    name: kindName.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    /**
     * HELE LISTA PÅ EN GANG, som rommene i et opphold — ikke én avkrysning om gangen.
     *
     * «Gjelder scener» redigeres som ett sett av avkrysninger og lagres som ett valg. Å sende én
     * scene per forespørsel ville gjort «bare utescenen» til fire forespørsler med tre
     * mellomtilstander, og den tredje av dem er «gjelder ingen scener» — en tilstand som betyr
     * noe helt annet enn den brukeren er på vei mot.
     */
    stageIds: z.array(z.uuid()).max(MAX_STAGES_PER_STAGE_TIME).optional(),
    occupiesStage: z.boolean().optional(),
    defaultMinutes: defaultMinutesSchema.nullable().optional(),
    stageLine: z.boolean().optional(),
  })
  .meta({ id: 'UpdateStageTimeKindRequest' });
export type UpdateStageTimeKindRequest = z.infer<typeof updateStageTimeKindRequestSchema>;

/**
 * The gaps a timetable item can carry. A CLOSED set, not free text, for the reason every error
 * code in this product is one: the API returns a code and the client renders it as
 * `performances.overview.warning.<code>`, so a value nobody wrote a translation for is a
 * compile error rather than a raw string in the middle of a Norwegian sentence.
 */
export const TIMETABLE_WARNINGS = ['missingDriver', 'missingPax', 'missingStage'] as const;
export const timetableWarningSchema = z.enum(TIMETABLE_WARNINGS);
export type TimetableWarning = z.infer<typeof timetableWarningSchema>;

export const MAX_TIMETABLE_ITEMS = 100;
export const MAX_TIMETABLE_TEXT = 200;

export const timetableItemSchema = z
  .object({
    /** The row's own id for a manual item; a derived key like `travel:<uuid>` otherwise. */
    id: z.string(),
    kind: timetableKindSchema,
    /** Fly, tog, buss, bil, båt — for ikonet og ordet på en reise. Null for alt annet. */
    travelMode: travelModeSchema.nullable(),
    /**
     * Which cue this is, or null for a derived item and for a free one somebody titled themselves.
     *
     * The NAME is not here — `title` already carries it, resolved from the cue on the read. An
     * organisation that renames «Push back» to «Publikum inn» renames it everywhere at once, and a
     * client that had to join two lists to draw one row could get that wrong.
     */
    stageTimeKindId: z.uuid().nullable(),
    title: z.string(),
    startsAt: z.string(),
    endsAt: z.string().nullable(),
    /**
     * DAG UTEN KLOKKE (215.15): en reise eller et måltid som bare har en dato. `startsAt` er
     * midnatt i årgangens sone, for plassering; klienten viser dagen uten klokkeslett.
     */
    dateOnly: z.boolean().optional(),
    location: z.string().nullable(),
    note: z.string().nullable(),
    /** «Mangler noe (f.eks. sjåfør) vises det som varsel på punktet.» A code, never a sentence. */
    warning: timetableWarningSchema.nullable(),
    /** «Settet markeres tydelig.» Sann for On stage-raden; Off stage er en vanlig rad (72). */
    isSet: z.boolean(),
    /** Which tab owns it, so the item can link to where its time is actually changed. */
    tab: z.string().nullable(),
  })
  .meta({ id: 'TimetableItem' });
export type TimetableItem = z.infer<typeof timetableItemSchema>;

/** One of the four cards: a count, and how much of it is missing something. */
export const overviewCountSchema = z
  .object({
    total: z.number().int(),
    /** «Uten akkreditering», «uten sjåfør», «forfalt oppgave» — the gap this card watches. */
    missing: z.number().int(),
  })
  .meta({ id: 'OverviewCount' });
export type OverviewCount = z.infer<typeof overviewCountSchema>;

/**
 * The three cards that carry a fact as well as a number.
 *
 * ---------------------------------------------------------------------------------------------
 * A COUNT IS NOT AN ANSWER, AND THAT IS WHAT ATLE SAW.
 *
 * 1. september: «De fire rutene er ikke så informativ. People kan inneholde antall i reiset.
 * Overnatting er hvor overnattingen er. Reise er kort hvordan de kommer seg hit, husk at ikke alle
 * reiser med fly.» A booker glancing at «5» under Reise learns that five journeys exist, which
 * they could have guessed; «5 · fly, bil» tells them what is actually happening, and the second
 * half of that sentence is the half you make a decision on.
 *
 * `modes` is a set of ENUM VALUES, not a sentence — the client renders each through
 * `performances.travel.mode.*`, so «ikke alle reiser med fly» is a fact the data carries rather
 * than an assumption the copy makes. `places` is the hotels' own names, which are free text and
 * therefore pass through untranslated.
 *
 * Both are DISTINCT and capped by what a card can hold: five is already more than the two lines
 * this fits in, and a booking with six hotels has a problem no card was going to solve.
 */
export const MAX_OVERVIEW_DETAIL = 5;

export const overviewPeopleSchema = overviewCountSchema
  .extend({
    /** «Antall i reiset» — the party that actually arrives, not everybody attached to the act. */
    travelling: z.number().int(),
  })
  .meta({ id: 'OverviewPeople' });
export type OverviewPeople = z.infer<typeof overviewPeopleSchema>;

export const overviewTravelSchema = overviewCountSchema
  .extend({ modes: z.array(travelModeSchema).max(MAX_OVERVIEW_DETAIL) })
  .meta({ id: 'OverviewTravel' });
export type OverviewTravel = z.infer<typeof overviewTravelSchema>;

export const overviewStaySchema = overviewCountSchema
  .extend({ places: z.array(z.string()).max(MAX_OVERVIEW_DETAIL) })
  .meta({ id: 'OverviewStay' });
export type OverviewStay = z.infer<typeof overviewStaySchema>;

export const overviewActivitySchema = z
  .object({
    id: z.uuid(),
    action: z.string(),
    entityType: z.string(),
    entityLabel: z.string().nullable(),
    actorName: z.string().nullable(),
    /**
     * HVILKEN ORGANISASJON SOM ENDRET (F25, 0761). Bare i en delt advance — der skriver to hus i det
     * samme, og «hvem, og fra hvilket hus» er det første spørsmålet. Null ellers.
     */
    actorOrganizationName: z.string().nullable(),
    /** Endringen ble gjort av plattformadmin inne som support (A11, 1042): «Navn (support)». */
    actorIsSupport: z.boolean().optional(),
    occurredAt: wireInstant,
    /*
     * HVA SOM BLE ENDRET (QA13 runde 3, blokk 2 a).
     *
     * Atle så fire like rader — «endret bookingen «Lemi»» — som ikke sa hva de gjaldt.
     * Organisasjonens egen aktivitetsliste har hatt feltnavnene hele tiden; bookingens kort
     * leste en port som ikke leverte dem. Navn, aldri verdier: en verdi kan være et honorar
     * eller et telefonnummer, under en rettighet denne fanen ikke har spurt om.
     */
    changedFields: z.array(z.string()),
  })
  .meta({ id: 'OverviewActivity' });
export type OverviewActivity = z.infer<typeof overviewActivitySchema>;

export const performanceOverviewDtoSchema = z
  .object({
    performanceId: z.uuid(),
    people: overviewPeopleSchema,
    travel: overviewTravelSchema,
    stay: overviewStaySchema,
    tasks: overviewCountSchema,
    /** Chronological. The page groups by day in the reader's own zone, not the server's. */
    timetable: z.array(timetableItemSchema),
    /*
     * The organisation's cues, in their order — the card draws a line per cue whether or not it
     * holds a time, so it needs the ones that are still empty. They ride along on the tab's one
     * read rather than on a second request under a second permission: an external with access to
     * one booking can see what its cues are called, and cannot see the settings screen that names
     * them.
     */
    stageTimeKinds: z.array(stageTimeKindDtoSchema),
    activity: z.array(overviewActivitySchema),
  })
  .meta({ id: 'PerformanceOverview' });
export type PerformanceOverviewDto = z.infer<typeof performanceOverviewDtoSchema>;

const shortText = z.string().trim().min(1).max(MAX_TIMETABLE_TEXT);

export const createTimetableItemRequestSchema = z
  .object({
    performanceId: z.uuid(),
    title: shortText,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    location: shortText.nullable(),
    note: shortText.nullable(),
  })
  .refine((body) => body.endsAt === null || body.endsAt >= body.startsAt, {
    error: 'time_order',
    path: ['endsAt'],
  })
  .meta({ id: 'CreateTimetableItemRequest' });
export type CreateTimetableItemRequest = z.infer<typeof createTimetableItemRequestSchema>;

export const updateTimetableItemRequestSchema = z
  .object({
    title: shortText,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    location: shortText.nullable(),
    note: shortText.nullable(),
  })
  .refine((body) => body.endsAt === null || body.endsAt >= body.startsAt, {
    error: 'time_order',
    path: ['endsAt'],
  })
  .meta({ id: 'UpdateTimetableItemRequest' });
export type UpdateTimetableItemRequest = z.infer<typeof updateTimetableItemRequestSchema>;

/**
 * Sett, endre eller tøm én scenetid.
 *
 * ---------------------------------------------------------------------------------------------
 * ONE ROUTE, THREE VERBS, BECAUSE THE SCREEN HAS ONE GESTURE.
 *
 * The card shows every cue the organisation keeps, whether or not it has a time. Somebody types 16:00 into
 * soundcheck; whether that is a `POST` or a `PATCH` depends on a row they cannot see and did not
 * ask about, and clearing the field is a `DELETE` of an id they never held. Making the client
 * work that out means it must first know whether the row exists — a read the server has to do
 * anyway, and a race the server would win and the client would not.
 *
 * So: `startsAt = null` clears the cue, anything else sets it, and the server decides which
 * statement that is. There is at most one row per (act, cue), enforced by a partial unique index
 * rather than by this handler being careful.
 */
export const setStageTimeRequestSchema = z
  .object({
    performanceId: z.uuid(),
    /** The organisation's own cue, by id — the seven are rows now, not an enum. */
    stageTimeKindId: z.uuid(),
    /** `null` removes the cue. The card's field is empty and stays empty. */
    startsAt: z.iso.datetime().nullable(),
    /** Soundcheck has a length; a get-in does not. */
    endsAt: z.iso.datetime().nullable(),
    location: z.string().trim().min(1).max(MAX_TIMETABLE_TEXT).nullable(),
    note: z.string().trim().min(1).max(MAX_TIMETABLE_TEXT).nullable(),
  })
  .refine(
    (body) => body.startsAt === null || body.endsAt === null || body.endsAt >= body.startsAt,
    {
      error: 'time_order',
      path: ['endsAt'],
    },
  )
  .refine((body) => body.startsAt !== null || body.endsAt === null, {
    error: 'end_without_start',
    path: ['endsAt'],
  })
  .meta({ id: 'SetStageTimeRequest' });
export type SetStageTimeRequest = z.infer<typeof setStageTimeRequestSchema>;

/**
 * The timetable grouped into days, in a named zone.
 *
 * ---------------------------------------------------------------------------------------------
 * SHARED, BECAUSE THE GROUPING IS A CORRECTNESS QUESTION RATHER THAN A LAYOUT ONE.
 *
 * A flight landing at 00:30 local is the 14th to the person meeting it and the 13th to UTC, and a
 * timetable that put it under the wrong heading would send somebody to the airport on the wrong
 * day. The zone is the ÅRGANG's, never the reader's browser — the same rule vedtak 0016 sets for
 * every other instant on this page.
 *
 * It lives here rather than in the panel so the same function can be asserted directly, without a
 * render, against the case that actually breaks: midnight.
 */
export function groupTimetableByDay(
  items: readonly TimetableItem[],
  timeZone: string,
): { day: string; items: TimetableItem[] }[] {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const days = new Map<string, TimetableItem[]>();
  for (const item of items) {
    /* `en-CA` is `YYYY-MM-DD`, which sorts as a string — the one locale that does. */
    const at = new Date(item.startsAt);
    /*
     * EN UGYLDIG DATO HOPPES OVER, den kaster ikke (N1, 20. september).
     *
     * `Intl.format` svarer ikke tomt på `Invalid Date` — den kaster `RangeError`, og her ville
     * kastet tatt hele kjøreplanen for én rad. Raden hører ikke til noen dag, og det er en rad som
     * mangler et tidspunkt: den skal vises som det, ikke som et tomt skjermbilde.
     */
    if (Number.isNaN(at.getTime())) continue;
    const day = formatter.format(at);
    const held = days.get(day);
    if (held === undefined) days.set(day, [item]);
    else held.push(item);
  }

  return [...days.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([day, dayItems]) => ({
      day,
      items: [...dayItems].sort((a, b) => (a.startsAt < b.startsAt ? -1 : 1)),
    }));
}
